import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { AudioMaster } from '../../components/AudioMaster';
import { StatusTag } from '../../components/StatusTag';
import { VideoStage } from '../../components/VideoStage';
import { clientStatusLabel, deliverableStage, isReviewable, metaLabel } from '../../utils/deliverables';
type Panel = 'idle' | 'confirm' | 'approved';
const EASE = [0.23, 1, 0.32, 1] as const;
export function EpisodeReview() {
  const {
    activeDeliverable,
    reviewDeliverables,
    approveDeliverable,
    requestChanges,
    goClient,
    notify,
    canApprove,
    canComment,
    projectComplete
  } = usePortal();
  const [panel, setPanel] = useState<Panel>('idle');
  const [composerOpen, setComposerOpen] = useState(false);
  const [changeNote, setChangeNote] = useState('');
  const [timecode, setTimecode] = useState('');
  if (!activeDeliverable) return null;
  const item = activeDeliverable;
  const index = reviewDeliverables.filter(entry => deliverableStage(entry.status) === deliverableStage(item.status)).findIndex(entry => entry.id === item.id);
  const number = index >= 0 ? String(index + 1).padStart(2, '0') : '';
  const inReview = isReviewable(item.status);
  const settled = item.status === 'Approved' || item.status === 'Delivered' || item.status === 'Ready for Download';
  const changesRequested = item.status === 'Changes Requested';
  const showVideo = item.type === 'Video' || item.type === 'Other' || item.type === 'Folder';
  const showAudio = item.type === 'Audio' || Boolean(item.audioUrl);

  /** Changes stay open for as long as this version is the current cut. */
  const canAddChanges = canComment && !projectComplete && !settled && (inReview || changesRequested);
  const currentChanges = item.changeRequests.filter(request => request.version === item.version);
  function confirmApproval() {
    approveDeliverable(item.id);
    setPanel('approved');
    setComposerOpen(false);
    notify('Portal notified');
  }
  function addChange(event: React.FormEvent) {
    event.preventDefault();
    const body = changeNote.trim();
    if (!body) return;
    requestChanges(item.id, body, timecode);
    setChangeNote('');
    setTimecode('');
    notify('Change added · Portal has been notified');
  }
  return <div data-id="element-458" data-mp="11c6qkpnezp9v">
      <button type="button" onClick={() => goClient('episodes')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-459" data-mp="11et0gvxs2gv9">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-460" data-mp="1fc97a8jzkcnj" />
        All Deliverables
      </button>

      <header className="mt-10 border-b border-rule pb-10" data-id="element-461" data-mp="3h3m7wlex7yds">
        <p className="label text-mute" data-id="element-462" data-mp="3h3upgqlznrvb">{metaLabel(item)}</p>
        <h1 className="mt-6 font-display text-4xl leading-[0.95] tracking-tight sm:text-7xl" data-id="element-463" data-mp="39kd0uytw0brw">
          {number && `${number} — `}
          {item.name.toUpperCase()}
        </h1>
        <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4" data-id="element-464" data-mp="2zu0jffhdcypz">
          <StatusTag status={item.status} audience="review" data-id="element-465" data-mp="2zrxk24ubtt8l" />
          {item.version && <p className="label m-0 text-mute" data-id="element-466" data-mp="2ux0cz3glxtyc">Version {item.version}</p>}
          {item.versionDate !== '—' && <p className="label m-0 text-mute" data-id="element-467" data-mp="3si64y4g8yrqv">{item.versionDate}</p>}
          {item.runtime !== '—' && <p className="label m-0 text-mute" data-id="element-468" data-mp="3nke1qea5jj7d">Runtime {item.runtime}</p>}
        </div>
      </header>

      <div className="mt-12 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-469" data-mp="3ij6x2o3b304x">
        <div className="lg:col-span-8" data-id="element-470" data-mp="3eiejxjrbcb3d">
          {showVideo && <>
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-471" data-mp="275uwcm2uexmz">
                <h2 className="label m-0 text-graphite" data-id="element-472" data-mp="232ydqvdaz1j1">Latest Review Version</h2>
                <p className="label-sm m-0 text-mute" data-id="element-473" data-mp="3r0fkfb7ky6f3">
                  {item.version ? `${item.version} · ` : ''}Plays inside Portal
                </p>
              </div>
              <div className="mt-6" data-id="element-474" data-mp="3h2jeqyxew3od">
                <VideoStage driveUrl={item.driveUrl} label={`${item.name} — ${item.version || 'Review copy'}`} runtime={item.runtime} data-id="element-475" data-mp="3h3kn2vn1mc2e" />
              </div>
            </>}

          {!showVideo && !showAudio && <div className="border border-rule px-7 py-9" data-id="element-476" data-mp="ijujmhp3qjn2">
              <p className="label m-0 text-graphite" data-id="element-477" data-mp="4l4np5e0qq0a">Latest Review Version</p>
              <p className="mt-5 font-display text-2xl" data-id="element-478" data-mp="4n7azhnse51b">{item.name}</p>
              {settled ? <p className="label mt-6 text-mute" data-id="element-479" data-mp="8osw1qah5pml">{clientStatusLabel(item.status)}</p> : <button type="button" onClick={() => notify('Simulated · would open in Google Drive')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-480" data-mp="2pdpyj170eqes">
                  Open for Review
                  <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-481" data-mp="2vqapv3p4iarr" />
                </button>}
            </div>}

          <div className="mt-12" data-id="element-482" data-mp="232y1qp3bduwc">
            <AnimatePresence mode="wait" initial={false} data-id="element-483" data-mp="233zohqtp05tw">
              {panel === 'approved' || settled && panel === 'idle' ? <motion.div key="approved" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-484" data-mp="1y5oe9ezhjtb5">
                  <p className="flex items-center gap-4 font-display text-3xl leading-none sm:text-4xl" data-id="element-485" data-mp="1kac7g9822cdv">
                    {item.status === 'Approved' ? 'Approved' : item.status}
                    <CheckIcon className="h-6 w-6" strokeWidth={1} data-id="element-486" data-mp="250zne6y2gpwb" />
                  </p>
                  <p className="label mt-5 text-mute" data-id="element-487" data-mp="291rw8z310ss2">
                    {item.status === 'Approved' ? `${item.version ? `${item.version} approved · ` : ''}Portal has been notified.` : 'Your final files are ready to download.'}
                  </p>
                  <button type="button" onClick={() => goClient('files')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-488" data-mp="1m3ygggkycfpq">
                    Go to Files
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-489" data-mp="2001ylbijwxlu" />
                  </button>
                </motion.div> : projectComplete ? <motion.div key="complete" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-490" data-mp="uv51dlrnnowv">
                  <p className="font-display text-3xl leading-none sm:text-4xl" data-id="element-491" data-mp="h3gr7o0yvaij">Delivered</p>
                  <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-492" data-mp="c4ndpvlojjid">
                    This project is complete. Final files are available to download.
                  </p>
                  <button type="button" onClick={() => goClient('files')} className="label group mt-8 flex items-center gap-3 border border-ink px-6 py-3.5 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-493" data-mp="77dnweudp5u0">
                    Go to Files
                    <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-494" data-mp="15ev1obwbeogc" />
                  </button>
                </motion.div> : panel === 'confirm' ? <motion.div key="confirm" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className="border border-ink px-7 py-9 sm:px-10" data-id="element-495" data-mp="10nd31uhrkzyj">
                  <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-496" data-mp="dpa1y1plb3wu">
                    Approve {item.version ? `version ${item.version}` : item.name}?
                  </h2>
                  <p className="mt-5 max-w-lg font-display text-lg text-charcoal" data-id="element-497" data-mp="2831asz03hjr8">
                    Portal will be notified and will prepare your final deliverables.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-498" data-mp="fg8s6sf8dwg">
                    <button type="button" onClick={confirmApproval} className="label border border-ink bg-ink px-7 py-4 text-paper transition-colors duration-150 ease-out hover:bg-charcoal" data-id="element-499" data-mp="3skahqjsu8h9m">
                      Confirm Approval
                    </button>
                    <button type="button" onClick={() => setPanel('idle')} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-500" data-mp="k2nqadgv1zer">
                      Cancel
                    </button>
                  </div>
                </motion.div> : canApprove || canAddChanges ? <motion.div key="actions" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} data-id="element-501" data-mp="2uws6917sl11x">
                  <div className="flex flex-wrap gap-5" data-id="element-502" data-mp="2098yyt8bq5of">
                    {canApprove && !projectComplete && !settled && <button type="button" onClick={() => setPanel('confirm')} className="label flex-1 border border-ink bg-ink px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-charcoal sm:flex-none" data-id="element-503" data-mp="1raf5vcu7bxfm">
                        Approve
                      </button>}
                    {canAddChanges && <button type="button" onClick={() => setComposerOpen(true)} className="label flex-1 border border-ink px-8 py-5 text-ink transition-colors duration-150 ease-out hover:bg-ink hover:text-paper sm:flex-none" data-id="element-504" data-mp="2a7ygum07zq4o">
                        {currentChanges.length > 0 ? 'Add Another Change' : 'Request Changes'}
                      </button>}
                  </div>

                  {changesRequested && <p className="label mt-6 text-mute" data-id="element-505" data-mp="3f8pqmmmheaa6">
                      Changes requested · Portal has been notified
                    </p>}
                </motion.div> : inReview ? <motion.p key="viewonly" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-506" data-mp="3oa5x4ja6ds94">
                  Your access is view only · approvals sit with your team&rsquo;s approver
                </motion.p> : <motion.p key="editing" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="label m-0 border border-rule px-7 py-8 text-mute" data-id="element-507" data-mp="awaypmu5mni6">
                  This is with Portal · nothing to review yet
                </motion.p>}
            </AnimatePresence>
          </div>

          {composerOpen && canAddChanges && <form onSubmit={addChange} className="mt-8 border border-ink px-7 py-9 sm:px-10" data-id="element-508" data-mp="2guhf9ybji5us">
              <h2 className="font-display text-2xl leading-tight sm:text-3xl" data-id="element-509" data-mp="2vivg63pebk8p">
                What would you like us to change?
              </h2>
              <div className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-start" data-id="element-510" data-mp="34lwma4e9hbcd">
                <label className="sm:w-32" data-id="element-511" data-mp="2zma55jvbbaya">
                  <span className="label block text-graphite" data-id="element-512" data-mp="2znla3n0huoqz">Timecode</span>
                  <input value={timecode} onChange={event => setTimecode(event.target.value)} placeholder="e.g. 00:17" inputMode="numeric" className="mt-4 w-full border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-513" data-mp="1a6k8qonq9w6e" />
                </label>
                <label className="flex-1" data-id="element-514" data-mp="383284amtqezs">
                  <span className="label block text-graphite" data-id="element-515" data-mp="3c4dwydxviw6x">Change Request</span>
                  <textarea value={changeNote} onChange={event => setChangeNote(event.target.value)} rows={3} placeholder="Describe the change required…" className="mt-4 w-full resize-none border-0 border-b border-rule bg-transparent pb-3 font-display text-xl text-ink placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-516" data-mp="2y54lfrkm4q9a" />
                </label>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-517" data-mp="reokg7kn7mhe">
                <button type="submit" className="label border border-ink px-7 py-4 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper" data-id="element-518" data-mp="rdvl3ro0jz9a">
                  Add Change
                </button>
                <button type="button" onClick={() => setComposerOpen(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-519" data-mp="3oufsz2qj11bl">
                  Done
                </button>
              </div>
            </form>}

          {currentChanges.length > 0 && <section aria-labelledby="requested-changes" className="mt-16" data-id="element-520" data-mp="364hmus6ahial">
              <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule pb-4" data-id="element-521" data-mp="362mwgyc90xz4">
                <h2 id="requested-changes" className="label m-0 text-graphite" data-id="element-522" data-mp="2w6jz05scdnii">
                  Requested Changes{item.version ? ` · ${item.version}` : ''}
                </h2>
                <p className="label-sm m-0 text-mute" data-id="element-523" data-mp="2s72t5b906oct">
                  {currentChanges.length} change{currentChanges.length === 1 ? '' : 's'}
                </p>
              </div>
              <ul className="m-0 list-none p-0" data-id="element-524" data-mp="1bmy2blnlnbci">
                {currentChanges.map(request => <li key={request.id} className="border-b border-rule py-6" data-id="element-525" data-mp="1go5ctsh4momv">
                    <div className="flex flex-wrap items-baseline gap-3" data-id="element-526" data-mp="1llnhh6o42hp2">
                      <span className="label text-ink" data-id="element-527" data-mp="nzgg05n3t5gx">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      {request.resolved && <span className="label-sm text-mute" data-id="element-528" data-mp="xwcgpp6rgnax">Completed</span>}
                    </div>
                    <p className="mt-2 max-w-2xl font-display text-xl leading-snug text-charcoal" data-id="element-529" data-mp="12omchluk4hl4">
                      {request.body}
                    </p>
                    <p className="label-sm mt-3 text-mute" data-id="element-530" data-mp="a7a2ysf8zelp">Sent {request.time}</p>
                  </li>)}
              </ul>
            </section>}

          {item.versions.some(entry => entry.visibleToClient) && <section aria-labelledby="previous-versions" className="mt-16" data-id="element-531" data-mp="3ua4xkhrm621m">
              <h2 id="previous-versions" className="label border-b border-rule pb-4 text-graphite" data-id="element-532" data-mp="bwx3cb2qufvy">
                Previous Versions
              </h2>
              <ul className="m-0 list-none p-0" data-id="element-533" data-mp="3fcm26wkfkql7">
                {item.versions.filter(entry => entry.visibleToClient).map(entry => <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-4 border-b border-rule py-5" data-id="element-534" data-mp="3pc32eygwyc9s">
                      <span className="flex items-baseline gap-5" data-id="element-535" data-mp="14l2ov01mg8wu">
                        <span className="font-display text-2xl leading-none" data-id="element-536" data-mp="14nfc9sq5fbha">{entry.version}</span>
                        <span className="label-sm text-mute" data-id="element-537" data-mp="zoc6g62ch189">
                          {entry.label} · {entry.date}
                          {entry.changeCount ? ` · ${entry.changeCount} change${entry.changeCount === 1 ? '' : 's'}` : ''}
                        </span>
                      </span>
                      <button type="button" onClick={() => notify(`Simulated · would play ${entry.version}`)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-538" data-mp="2djateavjgwdu">
                        Watch
                      </button>
                    </li>)}
              </ul>
            </section>}

          {showAudio && <section aria-labelledby="audio-review" className="mt-16" data-id="element-539" data-mp="3k6zkbkvufmnc">
              <h2 id="audio-review" className="label border-b border-rule pb-4 text-graphite" data-id="element-540" data-mp="3k4mxouf54o3y">
                Audio Review
              </h2>
              <AudioMaster label={`${item.name} — Audio ${item.version}`} driveUrl={item.audioUrl} data-id="element-541" data-mp="1ovgfb9df9kyk" />
            </section>}

        </div>

        <aside className="lg:col-span-4 lg:border-l lg:border-rule lg:pl-12" data-id="element-542" data-mp="1f4u74081bwpw">
          <h2 className="label border-b border-rule pb-4 text-graphite" data-id="element-543" data-mp="1a6aly219moyl">How Review Works</h2>
          <ol className="m-0 list-none p-0" data-id="element-544" data-mp="27u13otnxic8i">
            {[{
            step: '01',
            title: 'Watch',
            body: 'The latest version plays here inside Portal.'
          }, {
            step: '02',
            title: 'Respond',
            body: 'Add as many timecoded changes as you need.'
          }, {
            step: '03',
            title: 'Approve',
            body: 'Confirm the version once you are happy.'
          }, {
            step: '04',
            title: 'Download',
            body: 'Final deliverables move to Files after approval.'
          }].map(step => <li key={step.step} className="border-b border-rule py-6" data-id="element-545" data-mp="2u2zsnn59yhhd">
                <div className="flex items-baseline gap-4" data-id="element-546" data-mp="2y41ixeh7rj77">
                  <span className="label text-mute" data-id="element-547" data-mp="2y4l18was09lr">{step.step}</span>
                  <span className="font-display text-xl leading-none" data-id="element-548" data-mp="332wap5x5si3g">{step.title}</span>
                </div>
                <p className="mt-3 font-display text-base leading-snug text-graphite" data-id="element-549" data-mp="37uwgvg3to78h">{step.body}</p>
              </li>)}
          </ol>
          <p className="label-sm mt-7 leading-relaxed text-mute" data-id="element-550" data-mp="3cvaf66fmg2tb">
            Review copies stream from Google Drive · finals are delivered in Files
          </p>
        </aside>
      </div>
    </div>;
}